import { FC, PropsWithChildren, useState, useMemo } from 'react';

import { InspiredPaymentPortalAPIAccessibleAccountsAccessibleAccountDto as AccessibleAccount } from 'shared/models/payments/inspiredPaymentPortalAPIAccessibleAccountsAccessibleAccountDto';
import { useGetAccessibleAccounts } from 'shared/hooks/payment-api/accessible-accounts-endpoint';
import type { InspiredPaymentPortalAPISettingsFilterSettings } from 'shared/models/payments';
import { useGetFormattedSettings } from 'shared/hooks/useGetFormattedSettings';

import AccessibleAccountsContext from './AccessibleAccounts.context';
import {
  useObservableUserHeader,
  useGetInitialAccount,
  useSetDefaultAcademicYear
} from './AccessibleAccounts.hooks';

const AccessibleAccountsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data: settingsData } = useGetFormattedSettings();
  const { data, isLoading, error } = useGetAccessibleAccounts();
  const initialAccount = useGetInitialAccount(data?.accessibleAccounts || []);

  const [currentAccount, setCurrentAccount] = useState<AccessibleAccount | null>(initialAccount);

  const showOnlyAllAcademicFilterOption =
    settingsData?.visual.showOnlyAllAcademicYearsFilterOption || false;

  const isCurrentAccountObserver = useMemo(() => {
    return !currentAccount?.isCurrentUser || !currentAccount?.accountBillingCode;
  }, [currentAccount?.accountBillingCode, currentAccount?.isCurrentUser]);

  const availableAccounts = useMemo(() => {
    return (data?.accessibleAccounts || []).filter(
      account => account.personGuid !== currentAccount?.personGuid && account.accountBillingCode
    );
  }, [data?.accessibleAccounts, currentAccount?.personGuid]);

  const paymentRoutesDisabled = useMemo(
    () => !currentAccount?.filters.academicYears.length,
    [currentAccount?.filters.academicYears]
  );

  const defaultAcademicYear = useMemo(() => {
    if (showOnlyAllAcademicFilterOption) {
      return 'all';
    }

    if (!currentAccount?.filters.academicYears.length) {
      return '';
    }

    const newAcademicYears = [...currentAccount.filters.academicYears];

    return newAcademicYears.pop()?.value || '';
  }, [currentAccount?.filters.academicYears, showOnlyAllAcademicFilterOption]);

  const hasFilters = useMemo(() => {
    if (!currentAccount?.filters) {
      return false;
    }

    return Object.values(currentAccount.filters).some(filter => !!filter.length);
  }, [currentAccount?.filters]);

  const filters = useMemo(() => {
    if (!hasFilters) return {} as InspiredPaymentPortalAPISettingsFilterSettings;

    return currentAccount?.filters ?? ({} as InspiredPaymentPortalAPISettingsFilterSettings);
  }, [currentAccount?.filters, hasFilters]);

  useSetDefaultAcademicYear(defaultAcademicYear);
  useObservableUserHeader(isCurrentAccountObserver, currentAccount);

  const contextValue = useMemo(
    () => ({
      currentAccount,
      setCurrentAccount,
      shouldShowAccountSwitcher: !!availableAccounts.length && !!settingsData?.payorObserverEnabled,
      availableAccounts,
      isCurrentAccountObserver,
      hasFilters,
      filters,
      showOnlyAllAcademicFilterOption,
      defaultAcademicYear,
      paymentRoutesDisabled,
      isLoading,
      error
    }),
    [
      currentAccount,
      showOnlyAllAcademicFilterOption,
      availableAccounts,
      settingsData?.payorObserverEnabled,
      isCurrentAccountObserver,
      hasFilters,
      filters,
      defaultAcademicYear,
      paymentRoutesDisabled,
      isLoading,
      error
    ]
  );

  return (
    <AccessibleAccountsContext.Provider value={contextValue}>
      {children}
    </AccessibleAccountsContext.Provider>
  );
};

export default AccessibleAccountsProvider;
